import { useState } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
  Chip,
  Divider,
  Badge
} from '@heroui/react';
import SubscriptionPlanModal from './SubscriptionPlanModal';
import InvoiceDetailsModal from './InvoiceDetailsModal';
import PaymentRetryModal from './PaymentRetryModal';

const ConfigurationPaymentsAndSubscriptions = () => {
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  
  interface InvoiceItem {
    id: string;
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }

  interface InvoiceDetails {
    id: string;
    date: string;
    amount: string;
    status: string;
    plan: string;
    items: InvoiceItem[];
  }

  interface InvoiceItem {
    id: string;
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }

  interface InvoiceDetails {
    id: string;
    date: string;
    amount: string;
    status: string;
    plan: string;
    items: InvoiceItem[];
  }

  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceDetails | null>(null);
  const [isPaymentRetryModalOpen, setIsPaymentRetryModalOpen] = useState(false);
  const [retryInvoice, setRetryInvoice] = useState<InvoiceDetails | null>(null);
  
  // Mock data for subscriptions
  const currentSubscription = {
    plan: "Premium",
    status: "active",
    startDate: "2024-01-15",
    endDate: "2024-12-15",
    amount: "$99.99",
    billingCycle: "monthly",
    nextBillingDate: "2024-12-15",
    features: ["Almacenamiento ilimitado", "Soporte 24/7", "Usuarios ilimitados", "Integraciones premium"]
  };

  // Mock data for payment methods
  const paymentMethods = [
    {
      id: 1,
      type: "Visa",
      lastFour: "4242",
      expiration: "12/2027",
      isDefault: true
    },
    {
      id: 2,
      type: "Mastercard",
      lastFour: "5678",
      expiration: "08/2026",
      isDefault: false
    }
  ];

  // Mock data for invoices
  const invoices = [
    {
      id: "INV-001",
      date: "2024-11-01",
      amount: "$217.99",
      status: "failed",
      plan: "Premium"
    },
    {
      id: "INV-002",
      date: "2024-10-01",
      amount: "$99.99",
      status: "paid",
      plan: "Premium"
    },
    {
      id: "INV-003",
      date: "2024-09-01",
      amount: "$99.99",
      status: "paid",
      plan: "Premium"
    }
  ];

  // Type for invoice details
  type InvoiceDetailsMap = {
    [key: string]: InvoiceDetails;
  };

  // Mock data for invoice details
  const invoiceDetails: InvoiceDetailsMap = {
    "INV-001": {
      id: "INV-001",
      date: "2024-11-01",
      amount: "$217.99",
      status: "paid",
      plan: "Premium",
      items: [
        {
          id: "item-1",
          name: "Plan Premium Mensual",
          quantity: 1,
          unitPrice: 99.99,
          total: 99.99
        },
        {
          id: "item-2",
          name: "Usuarios Adicionales (10)",
          quantity: 10,
          unitPrice: 10000,
          total: 100000
        },
        {
          id: "item-3",
          name: "Documentos Adicionales (200)",
          quantity: 200,
          unitPrice: 500,
          total: 100000
        }
      ]
    },
    "INV-002": {
      id: "INV-002",
      date: "2024-10-01",
      amount: "$99.99",
      status: "paid",
      plan: "Premium",
      items: [
        {
          id: "item-1",
          name: "Plan Premium Mensual",
          quantity: 1,
          unitPrice: 99.99,
          total: 99.99
        }
      ]
    },
    "INV-003": {
      id: "INV-003",
      date: "2024-09-01",
      amount: "$99.99",
      status: "paid",
      plan: "Premium",
      items: [
        {
          id: "item-1",
          name: "Plan Premium Mensual",
          quantity: 1,
          unitPrice: 99.99,
          total: 99.99
        }
      ]
    }
  };

  const handleOpenPlanModal = () => {
    setIsPlanModalOpen(true);
  };

  const handlePaymentRetry = (paymentMethodId: number) => {
    // In a real implementation, this would call an API to retry the payment
    console.log(`Retry payment for invoice ${retryInvoice?.id} using payment method ${paymentMethodId}`);
    // Simulate successful payment
    alert(`Pago reintentado exitosamente para la factura ${retryInvoice?.id}`);
    // In a real app, you would update the invoice status after successful payment
  };

  return (
    <div className="space-y-6">
      {/* Subscription Status Card */}
      <Card className="w-full">
        <CardHeader className='border-b flex flex-direction-row justify-between items-center p-6'>
            <h3 className="text-xl font-semibold">Estado de Suscripción</h3>
            <p className="text-default-500">Administra tus pagos y suscripción</p>
        </CardHeader>
        <Divider />
        <CardBody className="py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Plan Actual</p>
              <p className="font-medium text-lg">{currentSubscription.plan}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Estado</p>
              <Chip 
                color={currentSubscription.status === 'active' ? 'success' : 'warning'}
                variant="flat"
                size="md"
              >
                {currentSubscription.status === 'active' ? 'Activa' : 'Inactiva'}
              </Chip>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Fecha de Inicio</p>
              <p className="font-medium">{new Date(currentSubscription.startDate).toLocaleDateString()}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Fecha de Finalización</p>
              <p className="font-medium">{new Date(currentSubscription.endDate).toLocaleDateString()}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Ciclo de Facturación</p>
              <p className="font-medium capitalize">{currentSubscription.billingCycle}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">Próxima Facturación</p>
              <p className="font-medium">{new Date(currentSubscription.nextBillingDate).toLocaleDateString()}</p>
            </div>
          </div>
          
          <div className="mt-6">
            <p className="text-sm text-gray-500 mb-2">Características del plan:</p>
            <div className="flex flex-wrap gap-2">
              {currentSubscription.features.map((feature, index) => (
                <Badge key={index} color="primary" variant="flat">
                  {feature}
                </Badge>
              ))}
            </div>
          </div>
        </CardBody>
        <CardFooter className="flex justify-between flex-wrap gap-2">
          <Button color="primary" variant="flat" onClick={handleOpenPlanModal}>
            Actualizar Plan
          </Button>
          <div className="flex gap-2">
            <Button color="warning" variant="bordered">Cancelar Suscripción</Button>
          </div>
        </CardFooter>
      </Card>

      {/* Payment Methods Card */}
      <Card className="w-full">
        <CardHeader>
          <h3 className="text-xl font-semibold">Métodos de Pago</h3>
        </CardHeader>
        <Divider />
        <CardBody className="py-4">
          <div className="space-y-4">
            {paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 dark:bg-gray-100/10">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-8 rounded flex items-center justify-center bg-gray-200 dark:bg-gray-700">
                    <span className="font-bold text-gray-800 dark:text-gray-200">{method.type.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium">{method.type} ending in {method.lastFour}</p>
                    <p className="text-sm text-gray-500">Expira {method.expiration}</p>
                  </div>
                  {method.isDefault && (
                    <Chip color="primary" size="sm" variant="flat">
                      Predeterminado
                    </Chip>
                  )}
                </div>
                <div className="flex space-x-2">
                  {!method.isDefault && (
                    <Button size="sm" variant="light">Establecer como predeterminado</Button>
                  )}
                  <Button size="sm" variant="light" color="danger">Eliminar</Button>
                </div>
              </div>
            ))}
          </div>
        </CardBody>
        <CardFooter className="flex justify-between">
          <Button variant="bordered">Agregar Nuevo Método</Button>
          <Button color="primary">Guardar Cambios</Button>
        </CardFooter>
      </Card>

      {/* Billing History */}
      <Card className="w-full">
        <CardHeader>
          <h3 className="text-xl font-semibold">Historial de Facturación</h3>
        </CardHeader>
        <Divider />
        <CardBody className="py-4">
          <Table aria-label="Historial de facturación" className="min-w-full">
            <TableHeader>
              <TableColumn>ID</TableColumn>
              <TableColumn>FECHA</TableColumn>
              <TableColumn>PLAN</TableColumn>
              <TableColumn>MONTO</TableColumn>
              <TableColumn>ESTADO</TableColumn>
              <TableColumn>ACCIONES</TableColumn>
            </TableHeader>
            <TableBody>
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>{invoice.id}</TableCell>
                  <TableCell>{new Date(invoice.date).toLocaleDateString()}</TableCell>
                  <TableCell>{invoice.plan}</TableCell>
                  <TableCell>{invoice.amount}</TableCell>
                  <TableCell>
                    <Chip 
                      color={invoice.status === 'paid' ? 'success' : invoice.status === 'pending' ? 'warning' : 'danger'}
                      variant="flat"
                      size="sm"
                    >
                      {invoice.status === 'paid' ? 'Pagado' : invoice.status === 'pending' ? 'Pendiente' : 'Fallido'}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="light" 
                        onClick={() => {
                          setSelectedInvoice(invoiceDetails[invoice.id]);
                          setIsInvoiceModalOpen(true);
                        }}
                      >
                        Ver Detalles
                      </Button>
                      {invoice.status === 'failed' && (
                        <Button 
                          size="sm" 
                          color="danger"
                          variant="flat"
                          onClick={() => {
                            setRetryInvoice(invoiceDetails[invoice.id]);
                            setIsPaymentRetryModalOpen(true);
                          }}
                        >
                          Reintentar Pago
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button variant="light" color="primary">Ver Facturas Anteriores</Button>
        </CardFooter>
      </Card>

      {/* Subscription Plan Modal */}
      <SubscriptionPlanModal 
        isOpen={isPlanModalOpen} 
        onClose={() => setIsPlanModalOpen(false)} 
      />

      {/* Invoice Details Modal */}
      <InvoiceDetailsModal 
        isOpen={isInvoiceModalOpen} 
        onClose={() => setIsInvoiceModalOpen(false)} 
        invoice={selectedInvoice} 
      />
      
      {/* Payment Retry Modal */}
      <PaymentRetryModal 
        isOpen={isPaymentRetryModalOpen} 
        onClose={() => setIsPaymentRetryModalOpen(false)} 
        invoice={retryInvoice} 
        paymentMethods={paymentMethods}
        onPaymentRetry={handlePaymentRetry}
      />
    </div>
  );
};

export default ConfigurationPaymentsAndSubscriptions;